import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api, authHeaders, errMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Plus, Tag, Pencil, Trash2, Pause, Play, Loader2, Copy, Repeat, RotateCcw, Clock, CalendarDays, ImagePlus, X, Ticket, CheckCircle2, Wallet, Globe, Store, BarChart3, Search, Star, GripVertical, ChevronLeft, ChevronRight } from "lucide-react";
import { discountLabel, isPerpetual, euro } from "@/lib/format";
import VendorOfferDetail from "@/components/VendorOfferDetail";
import CategoryCombobox from "@/components/CategoryCombobox";
import CoverCropDialog from "@/components/CoverCropDialog";
import { fileToDataUrl } from "@/lib/image";
import { it } from "date-fns/locale";
import { toast } from "sonner";

const DTYPES = [
  { v: "percent", label: "Sconto %" },
  { v: "fixed", label: "Importo € fisso" },
  { v: "price", label: "Prezzo speciale" },
  { v: "bogo", label: "2x1 / Omaggio" },
];

const DAYS = [
  { i: 0, l: "Lun" }, { i: 1, l: "Mar" }, { i: 2, l: "Mer" },
  { i: 3, l: "Gio" }, { i: 4, l: "Ven" }, { i: 5, l: "Sab" }, { i: 6, l: "Dom" },
];

const pad = (n) => String(n).padStart(2, "0");
const endOfTodayISO = () => { const d = new Date(); d.setHours(23, 59, 59, 0); return d.toISOString(); };
const startISO = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x.toISOString(); };
const endISO = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 0); return x.toISOString(); };
const fmtFull = (d) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
const chipCls = (active) => `h-11 rounded-xl font-bold text-sm transition-colors ${active ? "bg-[#E63946] text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`;

function DateRangeField({ value, onChange, testid, placeholder = "Seleziona il periodo" }) {
  const [open, setOpen] = useState(false);
  const label = value?.from
    ? value.to && value.to.getTime() !== value.from.getTime()
      ? `${fmtFull(value.from)} – ${fmtFull(value.to)}`
      : fmtFull(value.from)
    : placeholder;
  return (
    <div>
      <button type="button" data-testid={testid} onClick={() => setOpen((o) => !o)}
        className={`h-11 w-full rounded-xl border px-3 flex items-center gap-2 text-sm font-semibold transition-colors ${open ? "border-[#E63946]" : "border-slate-200"} ${value?.from ? "text-slate-800" : "text-slate-400"}`}>
        <CalendarDays className="h-4 w-4 text-[#E63946] shrink-0" /> <span className="truncate">{label}</span>
      </button>
      {open && (
        <div className="mt-2 rounded-xl border border-slate-200 bg-white shadow-sm flex flex-col items-center" data-testid={`${testid}-calendar`}>
          <Calendar mode="range" selected={value} onSelect={(r) => onChange(r || { from: undefined, to: undefined })}
            numberOfMonths={1} locale={it} weekStartsOn={1} disabled={{ before: new Date(new Date().setHours(0, 0, 0, 0)) }} />
          <div className="w-full flex justify-between items-center px-3 pb-2">
            <button type="button" onClick={() => onChange({ from: undefined, to: undefined })} className="h-8 px-2 rounded-lg text-sm font-semibold text-slate-400">Cancella</button>
            <button type="button" data-testid={`${testid}-done`} onClick={() => setOpen(false)} className="h-8 px-4 rounded-lg text-sm font-bold text-white bg-[#E63946]">Fatto</button>
          </div>
        </div>
      )}
    </div>
  );
}

const DAY_SHORT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const fmtDate = (d) => `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
const hm = (t) => { if (!t) return ""; const [h, m] = t.split(":"); return m === "00" ? h : `${h}:${m}`; };
function daysLabel(days) {
  const s = [...days].sort((a, b) => a - b);
  if (s.length === 7) return "Tutti i giorni";
  const contiguous = s.every((v, i) => i === 0 || v === s[i - 1] + 1);
  if (contiguous && s.length >= 3) return `${DAY_SHORT[s[0]]}–${DAY_SHORT[s[s.length - 1]]}`;
  return s.map((d) => DAY_SHORT[d]).join(", ");
}
function durationBadges(o) {
  const out = [];
  if (o.recurring) {
    const d = o.recur_days && o.recur_days.length ? daysLabel(o.recur_days) : "Ogni giorno";
    const time = o.recur_start && o.recur_end ? ` ${hm(o.recur_start)}–${hm(o.recur_end)}` : "";
    out.push({ key: "recur", icon: Repeat, label: `${d}${time}` });
  } else {
    const now = new Date();
    const starts = o.starts_at ? new Date(o.starts_at) : null;
    const exp = o.expires_at ? new Date(o.expires_at) : null;
    if (starts && starts > now) out.push({ key: "starts", icon: Clock, label: `Dal ${fmtDate(starts)}` });
    else if (exp) {
      const sameDay = exp.toDateString() === now.toDateString();
      out.push({ key: "exp", icon: Clock, label: sameDay ? "Solo oggi" : `Fino al ${fmtDate(exp)}` });
    }
    if (o.daily_start && o.daily_end) out.push({ key: "window", icon: Clock, label: `${hm(o.daily_start)}–${hm(o.daily_end)}` });
  }
  if (o.quantity) {
    const rem = o.remaining ?? Math.max(0, o.quantity - (o.click_count || 0));
    out.push({ key: "qty", icon: Tag, label: `${rem} ${rem === 1 ? "rimasto" : "rimasti"}`, warn: rem <= 3 });
  }
  return out;
}

function expiryTimeLabel(o) {
  if (o.recurring || !o.expires_at) return "";
  const exp = new Date(o.expires_at);
  const now = new Date();
  if (isNaN(exp)) return "";
  if (exp.toDateString() === now.toDateString()) {
    return `Scade alle ${String(exp.getHours()).padStart(2, "0")}:${String(exp.getMinutes()).padStart(2, "0")}`;
  }
  return "";
}

const STATUS = {
  active: { label: "Attiva", cls: "bg-emerald-50 text-emerald-700" },
  suspended: { label: "Sospesa", cls: "bg-amber-50 text-amber-700" },
  expired: { label: "Scaduta", cls: "bg-slate-100 text-slate-500" },
  archived: { label: "Archiviata", cls: "bg-slate-100 text-slate-400" },
};

const emptyForm = {
  title: "", category: "", discount_type: "percent", discount_value: 20, min_spend: 0,
  scope: "total", item_name: "",
  description: "", online: false, shop_url: "", images: [],
  duration_mode: "today", range: { from: undefined, to: undefined },
  recurring: false, recur_days: [], recur_start: "12:00", recur_end: "15:00", quantity: "",
  has_window: false, day_start: "12:00", day_end: "15:00",
};

export default function Offers() {
  const [params, setParams] = useSearchParams();
  const { vendor } = useAuth();
  const [offers, setOffers] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailOffer, setDetailOffer] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const addImages = async (e) => {
    const f = (e.target.files || [])[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Seleziona un file immagine."); return; }
    if (f.size > 25 * 1024 * 1024) { toast.error("Immagine troppo grande (oltre 25MB)."); return; }
    setUploadingImg(true);
    try {
      const src = await fileToDataUrl(f, 1600);
      setCropSrc(src);
      setCropOpen(true);
    } finally { setUploadingImg(false); }
  };
  const onCropped = (url) => {
    setForm((f) => ({ ...f, images: [...(f.images || []), url].slice(0, 5) }));
    setCropSrc(null);
  };
  const dragIdx = useRef(null);
  const reorderImages = (from, to) => {
    setForm((f) => {
      const imgs = [...(f.images || [])];
      if (from == null || to == null || from < 0 || to < 0 || from >= imgs.length || to >= imgs.length || from === to) return f;
      const [m] = imgs.splice(from, 1);
      imgs.splice(to, 0, m);
      return { ...f, images: imgs };
    });
  };
  const setCover = (i) => reorderImages(i, 0);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [catFilter, setCatFilter] = useState("all");
  const [sortBy, setSortBy] = useState("date");
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/vendor/offers", { headers: authHeaders("vendor") });
      setOffers(r.data.offers || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    load();
  }, [load]);

  useEffect(() => {
    if (params.get("new") === "1") { openNew(); setParams({}, { replace: true }); }
    // eslint-disable-next-line
  }, [params]);

  const openNew = () => {
    const vc = allTags.some((t) => t.id === vendor?.category) ? vendor.category : "";
    setEditing(null); setForm({ ...emptyForm, category: vc }); setError(""); setOpen(true);
  };

  const requestCategory = async (name) => {
    try {
      const r = await api.post("/vendor/category-requests", { name }, { headers: authHeaders("vendor") });
      const cat = r.data && r.data.category;
      if (!cat) return null;
      setCats((prev) => {
        const next = prev.map((m) => ({ ...m, tags: [...m.tags] }));
        const altro = next.find((m) => m.id === "altro") || next[next.length - 1];
        if (altro && !altro.tags.some((t) => t.id === cat.id)) {
          altro.tags.push({ id: cat.id, label: cat.label, provisional: cat.provisional });
        }
        return next;
      });
      if (r.data.already_exists) toast.success(`Categoria «${cat.label}» già disponibile, selezionata.`);
      else toast.success(`Categoria «${cat.label}» creata e in attesa di conferma dell'admin.`);
      return cat;
    } catch (e) {
      toast.error(errMessage(e) || "Richiesta categoria non riuscita");
      return null;
    }
  };
  const openEdit = (o) => {
    setEditing(o);
    const isRecurring = !!o.recurring;
    const perpetual = isPerpetual(o.expires_at);
    const range = { from: undefined, to: undefined };
    if (isRecurring) {
      if (o.starts_at) range.from = new Date(o.starts_at);
      if (o.expires_at && !perpetual) range.to = new Date(o.expires_at);
      if (!range.from && range.to) range.from = range.to;
    } else if (o.starts_at) {
      range.from = new Date(o.starts_at);
      range.to = o.expires_at ? new Date(o.expires_at) : undefined;
    }
    setForm({
      title: o.title, category: o.category, discount_type: o.discount_type || "percent",
      discount_value: o.discount_value ?? o.discount ?? 0, min_spend: o.min_spend || 0,
      scope: o.scope || "total", item_name: o.item_name || "",
      description: o.description || "", online: !!o.online, shop_url: o.shop_url || "", images: o.images || [],
      duration_mode: !isRecurring && o.starts_at ? "scheduled" : "today",
      range,
      recurring: isRecurring,
      recur_days: o.recur_days || [],
      recur_start: o.recur_start || "12:00",
      recur_end: o.recur_end || "15:00",
      has_window: !isRecurring && !!(o.daily_start && o.daily_end),
      day_start: o.daily_start || "12:00",
      day_end: o.daily_end || "15:00",
      quantity: o.quantity ?? "",
    });
    setError(""); setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.title) return setError("Inserisci un titolo.");
    if (!form.category) return setError("Scegli una categoria.");
    if (form.scope === "item" && !form.item_name.trim()) return setError("Inserisci il nome dell'articolo in offerta.");
    if (form.recurring) {
      if (!form.recur_days.length) return setError("Seleziona almeno un giorno di ricorrenza.");
      if (!(form.recur_start < form.recur_end)) return setError("L'ora di inizio deve precedere quella di fine.");
    } else if (form.duration_mode === "scheduled") {
      if (!form.range?.from) return setError("Seleziona il periodo dell'offerta.");
      const endD = form.range.to || form.range.from;
      if (endISO(endD) <= new Date().toISOString()) return setError("La scadenza deve essere futura.");
    }
    if (!form.recurring && form.has_window && !(form.day_start < form.day_end)) {
      return setError("La fascia oraria: l'ora di inizio deve precedere quella di fine.");
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title, category: form.category, discount_type: form.discount_type,
        discount_value: Number(form.discount_value),
        min_spend: form.scope === "item" ? 0 : Number(form.min_spend || 0),
        scope: form.scope, item_name: form.scope === "item" ? form.item_name.trim() : "",
        description: form.description, online: form.online, shop_url: form.shop_url, images: form.images || [],
        recurring: form.recurring,
        quantity: form.quantity === "" || Number(form.quantity) <= 0 ? null : Number(form.quantity),
      };
      if (form.recurring) {
        payload.recur_days = form.recur_days;
        payload.recur_start = form.recur_start;
        payload.recur_end = form.recur_end;
        payload.daily_start = null;
        payload.daily_end = null;
        if (form.range?.from) {
          payload.starts_at = startISO(form.range.from);
          payload.expires_at = endISO(form.range.to || form.range.from);
        } else {
          payload.starts_at = null;
          payload.expires_at = new Date(Date.now() + 3650 * 86400000).toISOString();
        }
      } else {
        payload.recur_days = [];
        payload.daily_start = form.has_window ? form.day_start : null;
        payload.daily_end = form.has_window ? form.day_end : null;
        if (form.duration_mode === "scheduled") {
          payload.starts_at = startISO(form.range.from);
          payload.expires_at = endISO(form.range.to || form.range.from);
        } else {
          payload.starts_at = null;
          payload.expires_at = endOfTodayISO();
        }
      }
      if (editing) await api.put(`/offers/${editing.offer_id}`, payload, { headers: authHeaders("vendor") });
      else await api.post("/offers", payload, { headers: authHeaders("vendor") });
      toast.success(editing ? "Offerta aggiornata" : "Offerta pubblicata!");
      setOpen(false);
      load();
    } catch (err) {
      setError(errMessage(err));
    } finally { setSaving(false); }
  };

  const toggleActive = async (o) => {
    await api.put(`/offers/${o.offer_id}`, { active: !o.active }, { headers: authHeaders("vendor") });
    load();
  };
  const remove = async (o) => {
    if (!window.confirm("Eliminare questa offerta?")) return;
    await api.delete(`/offers/${o.offer_id}`, { headers: authHeaders("vendor") });
    toast.success("Offerta eliminata");
    load();
  };
  const duplicate = async (o) => {
    await api.post(`/offers/${o.offer_id}/duplicate`, {}, { headers: authHeaders("vendor") });
    toast.success("Offerta duplicata");
    load();
  };
  const [relaunchTarget, setRelaunchTarget] = useState(null);
  const relaunch = (o) => setRelaunchTarget(o);
  const doRelaunch = async (reset) => {
    const o = relaunchTarget;
    setRelaunchTarget(null);
    if (!o) return;
    try {
      await api.post(`/offers/${o.offer_id}/relaunch`, { reset }, { headers: authHeaders("vendor") });
      toast.success(reset ? "Offerta ripartita da zero" : "Offerta ripresa");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };
  const toggleSelect = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const exitSelect = () => { setSelectMode(false); setSelected(new Set()); };
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Eliminare ${selected.size} offerte selezionate?`)) return;
    try {
      await Promise.all([...selected].map((id) => api.delete(`/offers/${id}`, { headers: authHeaders("vendor") })));
      toast.success(`${selected.size} offerte eliminate`);
      exitSelect();
      load();
    } catch (err) { toast.error("Errore durante l'eliminazione"); load(); }
  };

  const allTags = cats.flatMap((m) => m.tags.map((t) => ({ ...t, macro: m.label })));
  const isExpired = (o) => o.status === "expired" || o.status === "archived";
  const offerCats = Array.from(new Map(offers.map((o) => [o.category, o.category_label || o.category])).entries())
    .map(([id, label]) => ({ id, label }));
  const ql = q.trim().toLowerCase();
  const filtered = offers.filter((o) => {
    if (ql && !(`${o.title || ""} ${o.description || ""}`.toLowerCase().includes(ql))) return false;
    if (catFilter !== "all" && o.category !== catFilter) return false;
    return true;
  });
  const sortFn = (a, b) => {
    if (sortBy === "discount") return (b.discount || 0) - (a.discount || 0);
    if (sortBy === "redeemed") return (b.redeemed_count || 0) - (a.redeemed_count || 0);
    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
  };
  const activeOffers = filtered.filter((o) => !isExpired(o)).sort(sortFn);
  const expiredOffers = filtered.filter(isExpired).sort(sortFn);
  const showActive = statusFilter !== "expired";
  const showExpired = statusFilter !== "active";
  const cardHandlers = { setDetailOffer, openEdit, duplicate, toggleActive, remove, relaunch, selectMode, selected, toggleSelect };

  return (
    <div className="space-y-5 kn-fade-up">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Le mie offerte</h1>
          <p className="text-slate-500">{offers.filter((o) => o.is_live).length} attive · {offers.length} totali</p>
        </div>
        <Button onClick={openNew} data-testid="new-offer-btn" className="h-11 rounded-xl font-bold gap-2"><Plus className="h-4 w-4" /> Nuova</Button>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#E63946]" /></div>
      ) : offers.length === 0 ? (
        <div className="kn-card p-10 text-center">
          <div className="h-16 w-16 rounded-3xl bg-[#FFE4E6] flex items-center justify-center mx-auto mb-3"><Tag className="h-7 w-7 text-[#E63946]" /></div>
          <p className="font-bold text-slate-800">Nessuna offerta pubblicata</p>
          <p className="text-slate-500 text-sm mt-1 mb-4">Pubblica la tua prima offerta del giorno.</p>
          <Button onClick={openNew} className="rounded-xl font-bold gap-2"><Plus className="h-4 w-4" /> Crea offerta</Button>
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-2" data-testid="offers-filter-bar">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input data-testid="offers-search" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Cerca per titolo…" className="h-11 rounded-xl pl-9" />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger data-testid="offers-sort" className="h-11 rounded-xl w-[9.5rem]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="date">Più recenti</SelectItem>
                  <SelectItem value="discount">Sconto maggiore</SelectItem>
                  <SelectItem value="redeemed">Più riscatti</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="offers-filter-status" className="h-11 rounded-xl w-[8.5rem]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli stati</SelectItem>
                  <SelectItem value="active">Solo attive</SelectItem>
                  <SelectItem value="expired">Solo scadute</SelectItem>
                </SelectContent>
              </Select>
              <Select value={catFilter} onValueChange={setCatFilter}>
                <SelectTrigger data-testid="offers-filter-category" className="h-11 rounded-xl w-[9.5rem]"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">Tutte le categorie</SelectItem>
                  {offerCats.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant={selectMode ? "default" : "outline"} onClick={() => (selectMode ? exitSelect() : setSelectMode(true))}
                data-testid="offers-select-toggle" className="h-11 rounded-xl font-bold">
                {selectMode ? "Annulla" : "Seleziona"}
              </Button>
            </div>
          </div>

          {selectMode && (
            <div className="sticky top-2 z-10 kn-card p-2.5 flex items-center justify-between gap-2 border border-[#E63946]/30" data-testid="offers-bulk-bar">
              <span className="text-sm font-bold text-slate-700 pl-1">{selected.size} selezionate</span>
              <Button onClick={bulkDelete} disabled={selected.size === 0} data-testid="offers-bulk-delete"
                className="h-9 rounded-xl font-bold gap-1.5 bg-[#DC2626] hover:bg-[#b91c1c]"><Trash2 className="h-4 w-4" /> Elimina selezionate</Button>
            </div>
          )}

          {(showActive ? activeOffers.length : 0) + (showExpired ? expiredOffers.length : 0) === 0 ? (
            <div className="kn-card p-8 text-center" data-testid="offers-no-results">
              <p className="font-bold text-slate-700">Nessuna offerta trovata</p>
              <p className="text-slate-500 text-sm mt-1">Prova a cambiare ricerca o filtri.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {showActive && activeOffers.length > 0 && (
                <div className="space-y-2.5" data-testid="offers-active-section">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                    <h2 className="text-base font-extrabold text-slate-800">Offerte attive ({activeOffers.length})</h2>
                  </div>
                  {activeOffers.map((o) => <OfferCard key={o.offer_id} o={o} {...cardHandlers} />)}
                </div>
              )}
              {showExpired && expiredOffers.length > 0 && (
                <div className="space-y-2.5" data-testid="offers-expired-section">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-slate-400" />
                    <h2 className="text-base font-extrabold text-slate-500">Offerte scadute ({expiredOffers.length})</h2>
                  </div>
                  {expiredOffers.map((o) => <OfferCard key={o.offer_id} o={o} dimmed {...cardHandlers} />)}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {detailOffer && <VendorOfferDetail offer={detailOffer} onClose={() => setDetailOffer(null)} />}

      <CoverCropDialog
        open={cropOpen}
        image={cropSrc}
        aspect={16 / 9}
        outWidth={1280}
        title="Ritaglia la foto"
        subtitle="Proporzioni orizzontali 16:9. Trascina per posizionare e usa lo zoom."
        onOpenChange={(v) => { setCropOpen(v); if (!v) setCropSrc(null); }}
        onCropped={onCropped}
      />

      {/* Relaunch choice dialog */}
      <Dialog open={!!relaunchTarget} onOpenChange={(o) => { if (!o) setRelaunchTarget(null); }}>
        <DialogContent className="sm:max-w-md rounded-3xl" data-testid="relaunch-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-extrabold">Fai ripartire l'offerta</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 -mt-1">Come vuoi far ripartire <span className="font-semibold text-slate-700">"{relaunchTarget?.title}"</span>?</p>
          <div className="space-y-3 mt-2">
            <button
              data-testid="relaunch-reset"
              onClick={() => doRelaunch(true)}
              className="w-full text-left rounded-2xl border-2 border-[#E63946]/20 hover:border-[#E63946] bg-[#FFF1F2] p-4 transition-colors"
            >
              <div className="flex items-center gap-2 font-extrabold text-[#B91C3B]"><RotateCcw className="h-4 w-4" /> Riparti da zero</div>
              <p className="text-sm text-slate-600 mt-1">Nuovo ciclo dell'offerta: anche chi l'aveva già scaricata o usata può riscaricarla, come fosse nuova. Lo storico resta per le statistiche.</p>
            </button>
            <button
              data-testid="relaunch-resume"
              onClick={() => doRelaunch(false)}
              className="w-full text-left rounded-2xl border-2 border-slate-200 hover:border-slate-400 bg-white p-4 transition-colors"
            >
              <div className="flex items-center gap-2 font-extrabold text-slate-800"><Play className="h-4 w-4" /> Riprendi</div>
              <p className="text-sm text-slate-600 mt-1">Mantiene gli scaricamenti precedenti. Utile per una pausa o modifica breve: chi l'ha già usata non potrà riscaricarla.</p>
            </button>
          </div>
        </DialogContent>
      </Dialog>


      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="offer-dialog">
          <DialogHeader><DialogTitle className="text-xl font-extrabold">{editing ? "Modifica offerta" : "Nuova offerta"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3.5">
            <div className="space-y-1.5">
              <Label>Titolo</Label>
              <Input data-testid="offer-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Es. Sconto 20% sulla cena" className="h-11 rounded-xl" />
            </div>
            <div className="space-y-1.5">
              <Label>Categoria</Label>
              <CategoryCombobox
                value={form.category}
                options={allTags}
                onChange={(v) => setForm({ ...form, category: v })}
                onRequest={requestCategory}
              />
            </div>
            <div className="space-y-1.5">
              <Label>A cosa si applica lo sconto?</Label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="offer-scope-total"
                  onClick={() => setForm({ ...form, scope: "total" })}
                  className={`h-11 rounded-xl border font-bold text-sm transition-colors ${form.scope !== "item" ? "bg-[#E63946] text-white border-[#E63946]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
                  Sul totale del conto
                </button>
                <button type="button" data-testid="offer-scope-item"
                  onClick={() => setForm((f) => ({ ...f, scope: "item", discount_type: (f.discount_type === "percent" || f.discount_type === "fixed") ? f.discount_type : "percent", min_spend: 0 }))}
                  className={`h-11 rounded-xl border font-bold text-sm transition-colors ${form.scope === "item" ? "bg-[#E63946] text-white border-[#E63946]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
                  Su un singolo articolo
                </button>
              </div>
              <p className="text-xs text-slate-400">
                {form.scope === "item"
                  ? "Alla cassa inserirai quantità e prezzo dell'articolo: lo sconto verrà calcolato in automatico."
                  : "Lo sconto si applica all'intero conto del cliente."}
              </p>
            </div>
            {form.scope === "item" && (
              <div className="space-y-1.5">
                <Label>Nome articolo in offerta</Label>
                <Input data-testid="offer-item-name" value={form.item_name} onChange={(e) => setForm({ ...form, item_name: e.target.value })} placeholder="Es. Panino Classico" className="h-11 rounded-xl" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Tipo sconto</Label>
                <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger data-testid="offer-dtype" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{(form.scope === "item" ? DTYPES.filter((d) => d.v === "percent" || d.v === "fixed") : DTYPES).map((d) => <SelectItem key={d.v} value={d.v}>{d.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{form.discount_type === "percent" ? "Percentuale" : form.discount_type === "bogo" ? "—" : "Valore €"}</Label>
                <Input data-testid="offer-dvalue" type="number" min="0" value={form.discount_value} disabled={form.discount_type === "bogo"}
                  onChange={(e) => setForm({ ...form, discount_value: e.target.value })} className="h-11 rounded-xl" />
              </div>
            </div>
            {form.scope !== "item" && (
              <div className="space-y-1.5">
                <Label>Spesa minima € (opzionale)</Label>
                <Input data-testid="offer-minspend" type="number" min="0" value={form.min_spend} onChange={(e) => setForm({ ...form, min_spend: e.target.value })} className="h-11 rounded-xl" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Descrizione (opzionale)</Label>
              <Textarea data-testid="offer-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Dettagli e condizioni dell'offerta" className="rounded-xl" rows={3} />
            </div>

            <div className="space-y-1.5">
              <Label>Immagini (max 5)</Label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="offer-images-grid">
                {(form.images || []).map((img, i) => (
                  <div key={i} draggable
                    onDragStart={() => { dragIdx.current = i; }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); reorderImages(dragIdx.current, i); dragIdx.current = null; }}
                    onDragEnd={() => { dragIdx.current = null; }}
                    data-testid={`offer-image-${i}`}
                    className={`relative aspect-video rounded-xl overflow-hidden border group cursor-move ${i === 0 ? "border-[#E63946] ring-1 ring-[#E63946]" : "border-slate-200"}`}>
                    <img src={img} alt="" className="w-full h-full object-cover pointer-events-none" />
                    <span className="absolute top-1 left-1 h-5 w-5 rounded bg-black/45 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <GripVertical className="h-3.5 w-3.5" />
                    </span>
                    <button type="button" onClick={() => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }))}
                      data-testid={`offer-image-remove-${i}`} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center">
                      <X className="h-3.5 w-3.5" />
                    </button>
                    <div className="absolute bottom-1 left-1 right-1 flex items-center justify-between gap-1">
                      {i === 0 ? (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#E63946] text-white flex items-center gap-0.5"><Star className="h-2.5 w-2.5 fill-white" /> Copertina</span>
                      ) : (
                        <button type="button" onClick={() => setCover(i)} data-testid={`offer-image-cover-${i}`}
                          title="Imposta come copertina"
                          className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-white/90 text-slate-700 flex items-center gap-0.5 hover:bg-white">
                          <Star className="h-2.5 w-2.5" /> Copertina
                        </button>
                      )}
                      <span className="flex gap-0.5">
                        <button type="button" disabled={i === 0} onClick={() => reorderImages(i, i - 1)} data-testid={`offer-image-left-${i}`}
                          className="h-5 w-5 rounded bg-black/55 text-white flex items-center justify-center disabled:opacity-30">
                          <ChevronLeft className="h-3 w-3" />
                        </button>
                        <button type="button" disabled={i === (form.images || []).length - 1} onClick={() => reorderImages(i, i + 1)} data-testid={`offer-image-right-${i}`}
                          className="h-5 w-5 rounded bg-black/55 text-white flex items-center justify-center disabled:opacity-30">
                          <ChevronRight className="h-3 w-3" />
                        </button>
                      </span>
                    </div>
                  </div>
                ))}
                {(form.images || []).length < 5 && (
                  <label data-testid="offer-image-add" className="aspect-video rounded-xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400 cursor-pointer hover:border-[#E63946] hover:text-[#E63946]">
                    {uploadingImg ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
                    <span className="text-[10px] font-semibold mt-1">Aggiungi</span>
                    <input type="file" accept="image/*" className="hidden" onChange={addImages} />
                  </label>
                )}
              </div>
              <p className="text-xs text-slate-400">Formato consigliato: orizzontale <b>16:9</b>, max <b>2MB</b> (consigliato). La <b>prima foto è la copertina</b>: trascinala o usa le frecce per riordinare, o tocca "Copertina" su un'altra foto.</p>
            </div>

            {/* Durata */}
            <div className="space-y-2">
              <Label>Durata offerta</Label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="duration-today"
                  onClick={() => setForm({ ...form, duration_mode: "today", recurring: false })}
                  className={chipCls(!form.recurring && form.duration_mode === "today")}>Solo oggi</button>
                <button type="button" data-testid="duration-scheduled"
                  onClick={() => setForm({ ...form, duration_mode: "scheduled", recurring: false })}
                  className={chipCls(!form.recurring && form.duration_mode === "scheduled")}>Programmata</button>
              </div>
              {!form.recurring && form.duration_mode === "today" && (
                <p className="text-xs text-slate-400">Valida fino a fine giornata di oggi.</p>
              )}
            </div>
            {!form.recurring && form.duration_mode === "scheduled" && (
              <div className="space-y-1.5">
                <Label>Periodo (inizio – fine)</Label>
                <DateRangeField testid="offer-range" value={form.range} onChange={(r) => setForm({ ...form, range: r })} />
                <p className="text-xs text-slate-400">Tocca il giorno di inizio e poi quello di fine sullo stesso calendario.</p>
              </div>
            )}

            {/* Fascia oraria (pranzo/cena) — solo offerte non ricorrenti */}
            {!form.recurring && (
              <div className="space-y-3 p-3 rounded-xl bg-slate-50">
                <div className="flex items-center justify-between">
                  <div><p className="font-semibold text-slate-800">Valida solo in una fascia oraria</p><p className="text-xs text-slate-400">Es. solo a pranzo o solo a cena</p></div>
                  <Switch checked={form.has_window} data-testid="offer-window"
                    onCheckedChange={(v) => setForm({ ...form, has_window: v })} />
                </div>
                {form.has_window && (
                  <>
                    <div className="flex flex-wrap gap-1.5">
                      <button type="button" data-testid="window-preset-lunch"
                        onClick={() => setForm({ ...form, day_start: "12:00", day_end: "15:00" })}
                        className="h-8 px-3 rounded-lg text-sm font-bold bg-white border border-slate-200 text-slate-600 hover:bg-slate-100">Pranzo 12–15</button>
                      <button type="button" data-testid="window-preset-dinner"
                        onClick={() => setForm({ ...form, day_start: "19:00", day_end: "23:00" })}
                        className="h-8 px-3 rounded-lg text-sm font-bold bg-white border border-slate-200 text-slate-600 hover:bg-slate-100">Cena 19–23</button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5"><Label>Dalle</Label>
                        <Input type="time" data-testid="window-start" value={form.day_start} onChange={(e) => setForm({ ...form, day_start: e.target.value })} className="h-11 rounded-xl" /></div>
                      <div className="space-y-1.5"><Label>Alle</Label>
                        <Input type="time" data-testid="window-end" value={form.day_end} onChange={(e) => setForm({ ...form, day_end: e.target.value })} className="h-11 rounded-xl" /></div>
                    </div>
                    <p className="text-xs text-slate-400">L'offerta comparirà ai clienti solo in questa fascia oraria.</p>
                  </>
                )}
              </div>
            )}

            {/* Ricorrente */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
              <div><p className="font-semibold text-slate-800">Offerta ricorrente</p><p className="text-xs text-slate-400">Si ripete nei giorni e orari scelti</p></div>
              <Switch checked={form.recurring} data-testid="offer-recurring"
                onCheckedChange={(v) => setForm({ ...form, recurring: v, duration_mode: v ? "today" : form.duration_mode })} />
            </div>
            {form.recurring && (
              <div className="space-y-3 p-3 rounded-xl border border-slate-100">
                <div className="space-y-1.5">
                  <Label>Giorni</Label>
                  <div className="flex flex-wrap gap-1.5" data-testid="recur-days">
                    {DAYS.map((d) => {
                      const on = form.recur_days.includes(d.i);
                      return (
                        <button key={d.i} type="button" data-testid={`recur-day-${d.i}`}
                          onClick={() => setForm({ ...form, recur_days: on ? form.recur_days.filter((x) => x !== d.i) : [...form.recur_days, d.i] })}
                          className={`h-9 w-11 rounded-lg text-sm font-bold transition-colors ${on ? "bg-[#E63946] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>{d.l}</button>
                      );
                    })}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Dalle</Label>
                    <Input type="time" data-testid="recur-start" value={form.recur_start} onChange={(e) => setForm({ ...form, recur_start: e.target.value })} className="h-11 rounded-xl" /></div>
                  <div className="space-y-1.5"><Label>Alle</Label>
                    <Input type="time" data-testid="recur-end" value={form.recur_end} onChange={(e) => setForm({ ...form, recur_end: e.target.value })} className="h-11 rounded-xl" /></div>
                </div>
                <div className="space-y-1.5">
                  <Label>Periodo di validità (opzionale)</Label>
                  <DateRangeField testid="recur-range" value={form.range} onChange={(r) => setForm({ ...form, range: r })} placeholder="Sempre attiva" />
                  <p className="text-xs text-slate-400">Lascia vuoto per una ricorrenza senza scadenza, oppure scegli da/a per limitarla nel tempo (es. più settimane).</p>
                </div>
              </div>
            )}

            {/* Quantità */}
            <div className="space-y-1.5">
              <Label>Quantità disponibile (opzionale)</Label>
              <Input type="number" min="1" data-testid="offer-quantity" value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder="Illimitata" className="h-11 rounded-xl" />
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
              <div><p className="font-semibold text-slate-800">Offerta online</p><p className="text-xs text-slate-400">Codice fisso da usare sul tuo sito</p></div>
              <Switch checked={form.online} onCheckedChange={(v) => setForm({ ...form, online: v })} data-testid="offer-online" />
            </div>
            {form.online && (
              <div className="space-y-1.5">
                <Label>URL negozio online</Label>
                <Input data-testid="offer-shopurl" value={form.shop_url} onChange={(e) => setForm({ ...form, shop_url: e.target.value })} placeholder="https://..." className="h-11 rounded-xl" />
              </div>
            )}
            {error && <p className="text-sm text-[#DC2626] font-medium" data-testid="offer-error">{error}</p>}
            <Button type="submit" disabled={saving} data-testid="offer-save" className="w-full h-12 rounded-xl font-bold">
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : editing ? "Salva modifiche" : "Pubblica offerta"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OfferCard({ o, dimmed, setDetailOffer, openEdit, duplicate, toggleActive, remove, relaunch, selectMode, selected, toggleSelect }) {
  const st = STATUS[o.status] || STATUS.active;
  const checked = selected?.has(o.offer_id);
  return (
    <div data-testid={`vendor-offer-${o.offer_id}`}
      onClick={selectMode ? () => toggleSelect(o.offer_id) : undefined}
      className={`kn-card p-3.5 flex items-start gap-3 ${dimmed ? "opacity-70" : ""} ${selectMode ? "cursor-pointer" : ""} ${checked ? "ring-2 ring-[#E63946]" : ""}`}>
      {selectMode && (
        <input type="checkbox" checked={!!checked} readOnly data-testid={`offer-select-${o.offer_id}`}
          className="mt-1 h-5 w-5 accent-[#E63946] shrink-0" />
      )}
      <div className={`h-12 w-12 rounded-xl text-white flex items-center justify-center font-extrabold text-sm shrink-0 ${dimmed ? "bg-slate-400" : "kn-gradient"}`}>
        {discountLabel(o)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-bold text-slate-800 truncate">{o.title}</p>
          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
          {o.category_label && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{o.category_label}</span>}
        </div>
        {o.description && <p className="text-sm text-slate-500 mt-0.5 line-clamp-1">{o.description}</p>}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1" data-testid={`offer-claims-${o.offer_id}`}><Ticket className="h-3.5 w-3.5" /> {o.click_count} richieste</span>
          <span className="inline-flex items-center gap-1 text-emerald-600" data-testid={`offer-redeemed-${o.offer_id}`}><CheckCircle2 className="h-3.5 w-3.5" /> {o.redeemed_count ?? 0} riscattati</span>
          {o.min_spend > 0 && <span className="inline-flex items-center gap-1"><Wallet className="h-3.5 w-3.5" /> Min. {euro(o.min_spend)}</span>}
          <span className="inline-flex items-center gap-1">{o.online ? <><Globe className="h-3.5 w-3.5" /> Online</> : <><Store className="h-3.5 w-3.5" /> In negozio</>}</span>
          {expiryTimeLabel(o) && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {expiryTimeLabel(o)}</span>}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 mt-1.5" data-testid={`offer-badges-${o.offer_id}`}>
          {durationBadges(o).map((b) => {
            const BIcon = b.icon;
            return (
              <span key={b.key}
                className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${b.warn ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                <BIcon className="h-3 w-3" /> {b.label}
              </span>
            );
          })}
        </div>
      </div>
      <div className={`flex items-center gap-1 shrink-0 ${selectMode ? "hidden" : ""}`}>
        <IconBtn testid={`detail-${o.offer_id}`} onClick={() => setDetailOffer(o)} title="Dettagli e statistiche"><BarChart3 className="h-4 w-4" /></IconBtn>
        <IconBtn testid={`edit-${o.offer_id}`} onClick={() => openEdit(o)} title="Modifica"><Pencil className="h-4 w-4" /></IconBtn>
        <IconBtn testid={`dup-${o.offer_id}`} onClick={() => duplicate(o)} title="Duplica"><Copy className="h-4 w-4" /></IconBtn>
        {(o.status === "expired" || o.status === "archived") && (
          <IconBtn testid={`relaunch-${o.offer_id}`} onClick={() => relaunch(o)} title="Fai ripartire l'offerta"><RotateCcw className="h-4 w-4" /></IconBtn>
        )}
        {o.status !== "archived" && o.status !== "expired" && (
          <IconBtn testid={`toggle-${o.offer_id}`} onClick={() => toggleActive(o)} title={o.active ? "Sospendi" : "Riattiva"}>
            {o.active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </IconBtn>
        )}
        <IconBtn testid={`del-${o.offer_id}`} onClick={() => remove(o)} title="Elimina" danger><Trash2 className="h-4 w-4" /></IconBtn>
      </div>
    </div>
  );
}

function IconBtn({ children, onClick, title, danger, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} title={title}
      className={`h-9 w-9 rounded-lg flex items-center justify-center transition-colors ${danger ? "text-[#DC2626] hover:bg-red-50" : "text-slate-500 hover:bg-slate-100"}`}>
      {children}
    </button>
  );
}
